import React, { useEffect, useRef } from "react";
import type { CSSProperties } from "react";
import ScaleToFit from "./ScaleToFit";
import SideStarField from "./SideStarField";
import { useIsPortrait } from "./useIsPortrait";
import HTLDSlide7Mobile from "./HTLDSlide7Mobile";
import { useVisibleAnimation } from "./useVisibleAnimation";
import { FONT_BODY, FONT_DISPLAY, FONT_MONO } from "theme/fonts";
import { pick } from "i18n/lang";
import { slide7Content, useSlide7 } from "./slide7.data";

const accent = "#4fc3f7";
const font   = FONT_BODY;

const S: Record<string, CSSProperties> = {
root: {
background: "#040f18", fontFamily: font, color: "#d0e8f8",
width: "100%", height: "100%",
display: "flex", flexDirection: "column",
boxSizing: "border-box",
overflow: "hidden",
},
// Shared header row: breadcrumb (left) + slide title (right), above the divider line
subtitleBar: {
background: "#050f1a", borderBottom: "1px solid rgba(79,195,247,0.1)",
padding: "clamp(5px,0.6vw,9px) clamp(16px,3vw,40px)",
display: "flex", alignItems: "center", justifyContent: "space-between",
gap: "clamp(12px,2vw,28px)", flexShrink: 0,
},
body: {
flex: 1,
minHeight: 0,
display: "flex", gap: "clamp(16px,2vw,28px)",
padding: "clamp(10px,1.4vw,18px) clamp(16px,3vw,40px) clamp(10px,1.2vw,14px)",
alignItems: "stretch",
overflow: "hidden",
},
content: {
flex: 1, minWidth: "min(100%, 380px)",
display: "flex", flexDirection: "column",
justifyContent: "space-between",
},
panel: {
width: "clamp(220px,28vw,380px)", flexShrink: 0,
display: "flex", flexDirection: "column",
},
};

// Canvas-only drawing parameters, same order as slide7Content.waves
// (names, ranges, states and sleep-stage labels live in slide7.data.ts).
const waveDraw = [
  { freq: 9.0, amp: 12, ampLbl: "~5–10 μV"    },
  { freq: 6.0, amp: 14, ampLbl: "~10–20 μV"   },
  { freq: 3.8, amp: 18, ampLbl: "~20–60 μV"   },
  { freq: 2.2, amp: 26, ampLbl: "~20–100 μV"  },
  { freq: 0.9, amp: 32, ampLbl: "~100–200 μV" },
];

// ─── Wave Canvas ─────────────────────────────────────────────────────────────
const WaveCanvas: React.FC = () => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
const drawFnRef = useRef<null | (() => void)>(null);
const dtRef     = useRef<number>(0);
  useEffect(() => {
    const canvas = canvasRef.current; if (!canvas) return;
    const ctx = canvas.getContext("2d") as CanvasRenderingContext2D; if (!ctx) return;
    const W = 420, H = 560;
    const content = pick(slide7Content); // slides remount on language change
    const waves = content.waves.map((w, i) => ({ ...w, ...waveDraw[i], sleep: content.canvas.sleep[i] }));
  
    // ── DPR fix ──────────────────────────────────────────────────────────────
    const dpr = window.devicePixelRatio || 1;
    canvas.width = W * dpr; canvas.height = H * dpr;
    ctx.scale(dpr, dpr);
  
    let frame = 0;
    const rowH = 88, startY = 52, waveX = 80, waveW = 320;
  
    // Twinkling stars — original positions + extras in sky strip
    const starSeeds = [[18,20,0.9],[65,10,0.7],[360,18,1.0],[400,45,0.7],[35,70,0.8],[385,65,0.9],[410,12,0.9],[120,45,0.7]];
    const stars = [
      ...starSeeds.map(([x,y,r]) => ({ x, y, r, tw: Math.random()*Math.PI*2, spd: 0.010+Math.random()*0.022 })),
      ...Array.from({ length: 28 }, () => ({
        x: Math.random()*W, y: Math.random()*48,
        r: 0.3+Math.random()*0.9,
        tw: Math.random()*Math.PI*2, spd: 0.010+Math.random()*0.022,
      })),
    ];
  
    function draw() {
      ctx.clearRect(0, 0, W, H);
      ctx.fillStyle = "#040f18"; ctx.fillRect(0, 0, W, H);
  
      // ── Stars (twinkling) ─────────────────────────────────────────────────
      stars.forEach(s => {
        const tw = 0.25 + 0.75*Math.abs(Math.sin(frame*s.spd+s.tw));
        ctx.beginPath(); ctx.arc(s.x, s.y, s.r, 0, Math.PI*2);
        ctx.fillStyle=`rgba(255,255,255,${(tw*0.72).toFixed(2)})`; ctx.fill();
      });
  
      // ── Moon with halos ───────────────────────────────────────────────────
      [[16,0.07],[10,0.13],[7,1]].forEach(([r,a],i) => {
        ctx.beginPath(); ctx.arc(390,32,r,0,Math.PI*2);
        ctx.fillStyle = i<2 ? `rgba(255,252,180,${a})` : "#fffde7"; ctx.fill();
      });
  
      // ── Wave rows ─────────────────────────────────────────────────────────
      waves.forEach((w, i) => {
        const cy    = startY + i * rowH;
        const speed = frame * 0.025 * (w.freq * 0.4);
  
        // Row background
        if (w.htld) {
          ctx.fillStyle="rgba(79,195,247,0.07)"; ctx.fillRect(0, cy-rowH*0.45, W, rowH*0.9);
          // Left accent bar
          ctx.fillStyle=accent; ctx.fillRect(0, cy-rowH*0.45, 3, rowH*0.9);
        }
  
        // Row separator
        ctx.beginPath(); ctx.moveTo(0, cy-rowH*0.45); ctx.lineTo(W, cy-rowH*0.45);
        ctx.strokeStyle=`rgba(79,195,247,${w.htld?0.12:0.06})`; ctx.lineWidth=1; ctx.stroke();
  
        // Centre reference line
        ctx.beginPath(); ctx.moveTo(waveX,cy); ctx.lineTo(waveX+waveW,cy);
        ctx.strokeStyle=`rgba(79,195,247,${w.htld?0.14:0.07})`; ctx.lineWidth=1; ctx.stroke();
  
        // ── Wave path helper ─────────────────────────────────────────────
        const tracePath = () => {
          ctx.beginPath();
          for (let x=0; x<=waveW; x+=2) {
            const a = (x/waveW)*Math.PI*2*w.freq - speed;
            const y = cy + Math.sin(a)*w.amp;
            x===0 ? ctx.moveTo(waveX+x, y) : ctx.lineTo(waveX+x, y);
          }
        };
  
        // Area fill (HTLD only)
        if (w.htld) {
          ctx.save();
          tracePath();
          ctx.lineTo(waveX+waveW, cy); ctx.lineTo(waveX, cy); ctx.closePath();
          const aGrd = ctx.createLinearGradient(0, cy-w.amp, 0, cy+w.amp);
          aGrd.addColorStop(0,   "rgba(79,195,247,0.16)");
          aGrd.addColorStop(0.5, "rgba(79,195,247,0.06)");
          aGrd.addColorStop(1,   "rgba(79,195,247,0.01)");
          ctx.fillStyle=aGrd; ctx.fill();
          ctx.restore();
        }
  
        // Outer glow pass
        tracePath();
        ctx.strokeStyle = w.htld ? "rgba(79,195,247,0.15)" : "rgba(79,195,247,0.08)";
        ctx.lineWidth = w.htld ? 10 : 5; ctx.lineJoin="round"; ctx.stroke();
  
        // Mid glow pass
        tracePath();
        ctx.strokeStyle = w.htld ? "rgba(79,195,247,0.28)" : "rgba(79,195,247,0.15)";
        ctx.lineWidth = w.htld ? 4.5 : 2.2; ctx.lineJoin="round"; ctx.stroke();
  
        // Core line
        tracePath();
        ctx.strokeStyle = w.htld ? accent : "rgba(79,195,247,0.68)";
        ctx.lineWidth = w.htld ? 2.2 : 1.5; ctx.lineJoin="round"; ctx.stroke();
  
        // Traveling shimmer dot on HTLD wave
        if (w.htld) {
          const sx = waveX + ((frame * 1.8) % waveW);
          const sa = ((sx-waveX)/waveW)*Math.PI*2*w.freq - speed;
          const sy = cy + Math.sin(sa)*w.amp;
          const sg = ctx.createRadialGradient(sx,sy,0,sx,sy,14);
          sg.addColorStop(0,   "rgba(255,255,255,0.88)");
          sg.addColorStop(0.3, "rgba(79,195,247,0.55)");
          sg.addColorStop(1,   "rgba(79,195,247,0)");
          ctx.beginPath(); ctx.arc(sx,sy,14,0,Math.PI*2); ctx.fillStyle=sg; ctx.fill();
          ctx.beginPath(); ctx.arc(sx,sy,3,0,Math.PI*2); ctx.fillStyle="#ffffff"; ctx.fill();
        }
  
      // ── Text — repositioned to fit 5 lines per row ──────────────────────────

      // Wave name — moved up so all 5 lines centre nicely in the row
      ctx.font = "bold 15px 'JetBrains Mono','Courier New',monospace"; ctx.textAlign = "left";
      ctx.fillStyle = w.htld ? accent : "rgba(130,215,240,0.92)";
      ctx.fillText(w.name, 8, cy - 20);

      // Frequency range
      ctx.font = "10px 'JetBrains Mono','Courier New',monospace";
      ctx.fillStyle = w.htld ? "rgba(79,195,247,0.95)" : "rgba(100,200,230,0.65)";
      ctx.fillText(w.range, 8, cy - 6);

      // Sleep stage
      ctx.fillStyle = w.htld ? "rgba(79,195,247,0.92)" : "rgba(79,195,247,0.58)";
      ctx.fillText(w.sleep, 8, cy + 8);

      // Amplitude
      ctx.fillStyle = w.htld ? "rgba(79,195,247,0.80)" : "rgba(79,195,247,0.45)";
      ctx.fillText(w.ampLbl, 8, cy + 22);

      // HTLD marker
      if (w.htld) {
          ctx.font = "bold 10px 'JetBrains Mono','Courier New',monospace";
          ctx.fillStyle = accent;
          ctx.fillText(content.canvas.marker, 8, cy + 36);
      }
      });
  
frame += dtRef.current > 0 ? dtRef.current * 60 : 1;
    }
  
    drawFnRef.current = draw;
    return () => { drawFnRef.current = null; };
  }, []);
  
useVisibleAnimation(canvasRef, (dt) => { dtRef.current = dt; drawFnRef.current?.(); }, { fps: 30 });

  return (
    <div style={{ width: "100%", height: "100%", display: "flex", alignItems: "center", justifyContent: "center", overflow: "hidden" }}>
      <canvas
        ref={canvasRef}
        style={{ display: "block", borderRadius: 8, width: "100%", height: "100%", objectFit: "contain" }}
      />
    </div>
  );
  };

// ─── Slide content (authored at the ScaleToFit design size) ────────────────────
const HTLDSlide7Inner: React.FC = () => {
const { meta, waves, callout } = useSlide7();
const fontLinkRef = useRef<HTMLLinkElement | null>(null);

useEffect(() => {
const existing = document.querySelector(
  'link[href*="Space+Grotesk"]'
) as HTMLLinkElement | null;

if (!existing) {
  const link = document.createElement("link");
  link.rel  = "stylesheet";
  link.href = "https://fonts.googleapis.com/css2?family=Merriweather:ital,wght@0,300;0,400;0,700;1,300;1,400;1,700&display=swap";
  document.head.appendChild(link);
  fontLinkRef.current = link;
}

return () => {
  if (fontLinkRef.current && document.head.contains(fontLinkRef.current)) {
    document.head.removeChild(fontLinkRef.current);
    fontLinkRef.current = null;
  }
};
}, []);

const lbl: CSSProperties = {
fontSize: "clamp(8px,0.7vw,11px)", fontFamily: FONT_MONO,
color: accent, letterSpacing: 3, textTransform: "uppercase",
marginBottom: "clamp(4px,0.55vw,8px)", fontStyle: "normal",
};
const txt: CSSProperties = {
fontSize: "clamp(12px,1.0vw,15px)", fontStyle: "normal", fontFamily: FONT_BODY,
lineHeight: 1.7, color: "#cde0f2", margin: 0,
};

const rootRef = useRef<HTMLDivElement>(null);

return (
<div style={S.root} ref={rootRef}>      {/* add ref={rootRef} */}
<SideStarField rootRef={rootRef} />   {/* add this line */}

  {/* Header row: breadcrumb (left) + slide title (right), above the horizontal line */}
  <div style={S.subtitleBar}>
    <div style={{
      fontSize: "clamp(11px,1.0vw,14px)", fontStyle: "normal", color: "#7aa8cc",
      fontFamily: FONT_BODY,
    }}>
      {meta.breadcrumbPre} &nbsp;·&nbsp;{" "}
      <span style={{ color: accent }}>{meta.breadcrumbAccent}</span>
    </div>
    <div style={{
      fontSize: "clamp(16px,1.9vw,28px)", fontStyle: "normal", fontWeight: 600,
      color: "#e8f4ff", fontFamily: FONT_DISPLAY, letterSpacing: 0.5, lineHeight: 1,
      whiteSpace: "nowrap",
    }}>
      {meta.titlePlain}<span style={{ color: accent }}>{meta.titleAccent}</span>
    </div>
  </div>

  {/* Body */}
  <div style={S.body}>

    {/* Content */}
    <div style={S.content}>

      {/* Title block */}
      <div>
        <h1 style={{ fontFamily: FONT_DISPLAY, fontSize: "clamp(19px,2.3vw,30px)", fontStyle: "normal", fontWeight: 600, color: "#e8f4ff", margin: "0 0 clamp(2px,0.35vw,6px)", lineHeight: 1.2 }}>
          {meta.titlePlain}<span style={{ color: accent }}>{meta.titleAccent}</span>
        </h1>
        <p style={{ fontFamily: FONT_BODY, fontSize: "clamp(12px,1.1vw,15px)", fontStyle: "normal", fontWeight: 300, color: "#7aa8cc", margin: 0, letterSpacing: 0.3 }}>
          {meta.subtitle}
        </p>
      </div>

      {/* Wave cards */}
      <div style={{ borderBottom: "1px solid rgba(79,195,247,0.10)", paddingBottom: "clamp(8px,1vw,14px)" }}>
        <div style={lbl}>{meta.statesLabel}</div>
        <div style={{ display: "flex", flexDirection: "column", gap: "clamp(5px,0.7vw,9px)" }}>
          {waves.map((w, i) => (
            <div key={i} style={{ display: "flex", gap: "clamp(10px,1.2vw,16px)", alignItems: "flex-start", background: w.htld ? "rgba(79,195,247,0.07)" : "rgba(255,255,255,0.02)", border: `1px solid ${w.htld ? "rgba(79,195,247,0.30)" : "rgba(79,195,247,0.08)"}`, borderRadius: 7, padding: "clamp(7px,0.9vw,11px) clamp(10px,1.1vw,14px)" }}>
              <div style={{ flexShrink: 0, width: "clamp(46px,4.6vw,62px)" }}>
                <div style={{ fontFamily: FONT_BODY, fontSize: "clamp(12px,1.0vw,15px)", fontStyle: "normal", fontWeight: 700, color: w.htld ? accent : "rgba(79,195,247,0.75)" }}>{w.name}</div>
                <div style={{ fontSize: "clamp(8px,0.65vw,10px)", fontFamily: FONT_MONO, color: "#4a6880", marginTop: 2 }}>{w.range}</div>
              </div>
              <div style={{ flex: 1 }}>
                <p style={{ fontFamily: FONT_BODY, fontSize: "clamp(11px,0.92vw,14px)", fontStyle: "normal", fontWeight: 300, lineHeight: 1.55, color: w.htld ? "#90dcf8" : "#9bbdd6", margin: 0 }}>{w.state}</p>
              </div>
              {w.htld && (
                <div style={{ flexShrink: 0, fontSize: "clamp(8px,0.65vw,10px)", fontFamily: FONT_MONO, color: accent, background: "rgba(79,195,247,0.12)", padding: "3px 8px", borderRadius: 4, alignSelf: "center" }}>
                  {meta.badge}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Callout */}
      <div style={{ background: "rgba(79,195,247,0.06)", border: "1px solid rgba(79,195,247,0.20)", borderRadius: 8, padding: "clamp(11px,1.3vw,18px) clamp(13px,1.5vw,20px)" }}>
        <div style={lbl}>{meta.calloutLabel}</div>
        <p style={{ ...txt, color: "#9adcf8" }}>
        {callout.map((sg, i) => sg.b
          ? <span key={i} style={{ color: "#c8f0ff", fontWeight: 700 }}>{sg.t}</span>
          : <React.Fragment key={i}>{sg.t}</React.Fragment>)}
        </p>
      </div>

    </div>

    {/* Panel — wave canvas fills the full panel height */}
    <div style={S.panel}>
      <div style={{ flex: 1, minHeight: 0, display: "flex", alignItems: "center", justifyContent: "center" }}>
        <WaveCanvas />
      </div>
    </div>

  </div>

</div>
);
};

// Scale the whole slide as one unit so it looks identical across screen
// resolutions, window sizes, and browser zoom — no reflow, no clipping.
const HTLDSlide7: React.FC = () => {
  const portrait = useIsPortrait();
  return portrait
    ? <HTLDSlide7Mobile />
    : <ScaleToFit><HTLDSlide7Inner /></ScaleToFit>;
};

export default HTLDSlide7;